import type { MetricValue, UserRef } from '@ops/contracts'

import { plural } from '../../lib/format'
import type { ItemDeDeuda } from './deuda'
import {
  Button,
  Card,
  EmptyMetricState,
  Icon,
  ProvenanceBadge,
  Rule,
  Stack,
  StatusPill,
} from '../../ui'

/**
 * La deuda de datos como **frente de primera clase**: no poder medir es estar
 * roto. Es el bloque que ataca el problema 1 del spec (deuda de datos invisible)
 * y por eso no está escondido en una nota al pie.
 *
 * Dos listas distintas, porque son dos problemas distintos:
 *  - **sin dato**: nadie lo cargó nunca en el período → `EmptyMetricState` con
 *    la acción de cargarlo. Jamás un "—" ni un cero.
 *  - **vencido**: hay número pero la carga tiene más de 30 días → el badge lo
 *    dice en ámbar y con la palabra "vencido".
 *
 * Cuando una métrica no tiene ninguna carga registrada, el badge va con
 * `provenance={null}`: "fuente sin declarar · cargalo en Admin". No se le
 * inventa una fuente a un dato que no la tiene (injerto 6 del briefing).
 *
 * Las dos listas son `ItemDeDeuda` y no `MetricValue` porque la deuda se deriva
 * de las **alertas** del payload (ver `deuda.ts`), y la API alerta sobre todo el
 * universo de métricas del rol, no solo sobre las cinco de la fila héroe. Un
 * ítem que la pantalla conoce únicamente por su alerta llega con `metric: null`:
 * se lista igual, pero sin proveniencia inventada y sin botón de carga, porque
 * sin la unidad ni el período de la métrica el diálogo no se puede armar.
 */
export interface DeudaDatosCardProps {
  id?: string
  sinDato: readonly ItemDeDeuda[]
  vencidas: readonly ItemDeDeuda[]
  /** Ausente = el rol no puede cargar: no se ofrece una acción que la API rechaza. */
  onCargar?: (metric: MetricValue) => void
  ownerDe: (slug: string) => UserRef | null
  /** Reloj compartido de la pantalla, para que la frescura sea consistente. */
  now: Date
}

export function DeudaDatosCard({
  id,
  sinDato,
  vencidas,
  onCargar,
  ownerDe,
  now,
}: DeudaDatosCardProps) {
  const total = sinDato.length + vencidas.length
  const limpio = total === 0

  return (
    <Card
      as="section"
      ariaLabel="Deuda de datos"
      padding="md"
      severidad={limpio ? 'ok' : 'warning'}
      punteada={!limpio}
    >
      <div id={id} className="scroll-mt-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="inline-flex items-center gap-2 text-titulo font-medium text-text">
            <Icon name="sin-dato" size={14} className="text-muted-strong" />
            {limpio
              ? 'No hay deuda de datos'
              : `${total} ${plural(total, 'número que hoy no se puede leer', 'números que hoy no se pueden leer')}`}
          </h3>
          <StatusPill severidad={limpio ? 'ok' : 'warning'} caps size="sm">
            {limpio ? 'Al día' : 'Deuda de datos'}
          </StatusPill>
        </div>

        <p className="mb-3 max-w-prosa text-meta text-muted-strong">
          {limpio
            ? 'Todas las métricas del período tienen dato cargado y con menos de 30 días. No hay nada que saldar.'
            : 'Un número que nadie carga no es un cero: es un frente que no se puede medir. Mientras falte, el semáforo de esa métrica no dice nada.'}
        </p>

        {sinDato.length > 0 ? (
          <>
            <p className="rotulo mb-2">Sin dato en el período</p>
            <Stack as="ul" gap="sm" className="mb-3">
              {sinDato.map((item) => {
                const owner = ownerDe(item.slug)
                const metric = item.metric
                return (
                  <li
                    key={item.slug}
                    data-testid="deuda-sin-dato"
                    className="flex min-w-0 flex-wrap items-start justify-between gap-2 border-b border-dashed border-n300 pb-2 last:border-b-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="text-meta font-medium text-text">
                        {item.nombre}
                      </p>
                      <p className="text-micro text-muted-strong">
                        {owner === null
                          ? 'Sin dueño asignado en el catálogo de métricas'
                          : `Dueño: ${owner.nombre}`}
                      </p>
                      {/* Sin carga registrada la fuente no está declarada, y se
                          dice así en vez de atribuirle una. */}
                      <div className="mt-1">
                        <ProvenanceBadge
                          provenance={
                            metric === null || metric.provenance.updatedAt === null
                              ? null
                              : metric.provenance
                          }
                          now={now}
                          hrefAdmin="/admin"
                          variant="inline"
                        />
                      </div>
                    </div>
                    <EmptyMetricState
                      metricNombre={item.nombre}
                      onLoadData={
                        onCargar === undefined || metric === null
                          ? undefined
                          : () => onCargar(metric)
                      }
                      size="sm"
                    />
                  </li>
                )
              })}
            </Stack>
          </>
        ) : null}

        {vencidas.length > 0 ? (
          <>
            {sinDato.length > 0 ? <Rule spacing="sm" punteada /> : null}
            <p className="rotulo mb-2">Con carga vencida</p>
            <Stack as="ul" gap="sm">
              {vencidas.map((item) => {
                const metric = item.metric
                return (
                  <li
                    key={item.slug}
                    data-testid="deuda-vencida"
                    className="flex min-w-0 flex-wrap items-center justify-between gap-2"
                  >
                    <span className="text-meta font-medium text-text">
                      {item.nombre}
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <ProvenanceBadge
                        provenance={metric === null ? null : metric.provenance}
                        now={now}
                        hrefAdmin="/admin"
                      />
                      {onCargar !== undefined && metric !== null ? (
                        <Button
                          variante="chico"
                          icono="lapiz"
                          onClick={() => onCargar(metric)}
                        >
                          Actualizar
                          <span className="sr-only"> {item.nombre}</span>
                        </Button>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </Stack>
          </>
        ) : null}
      </div>
    </Card>
  )
}
